import { defineComponent } from 'vue';
import _ from 'lodash';
import { HasTaskMixin } from '@/mixins';
import { DetailedUser, UserFilters, QueryResult } from '@/type';
import repository from './user-repository';

interface Data {
  page: number,
  limit: number,
  total: number,
  users: DetailedUser[],
  task: Promise<QueryResult>|null,
  delayedLoad?: Function,
}

export default defineComponent({
  emits: ['selection'],
  mixins: [HasTaskMixin],
  props: {
    accountId: {
      type: Number,
      required: true,
    },
    keyword: String,
    excludeSelf: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Array as () => DetailedUser[],
      default() {
        return [];
      },
    },
  },
  data(): Data {
    return {
      page: 1,
      limit: 10,
      total: 0,
      users: [],
      task: null,
    };
  },
  computed: {
    offset(): number {
      return (this.page - 1) * this.limit;
    },
    filters(): UserFilters {
      return {
        keyword: this.keyword,
        excludeSelf: this.excludeSelf,
        offset: this.offset,
        limit: this.limit,
      };
    },
  },
  watch: {
    accountId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
    filters: 'load',
  },
  methods: {
    load() {
      this.task = repository.findAll(this.accountId, this.filters);
      this.task.then((result: QueryResult) => {
        this.total = result.total;
        this.users = (result.items as DetailedUser[]);
      });
    },
    isSelected(id: number): boolean {
      return !_.isUndefined(this.selected.find(u => u.id === id));
    },
    toggleSelect(user: DetailedUser) {
      if (this.isSelected(user.id)) {
        this.deselect(user);
      } else {
        this.select(user);
      }
    },
    select(user: DetailedUser) {
      if (!this.isSelected(user.id)) {
        const selected = [...this.selected];
        selected.push(user);
        this.$emit('selection', selected);
      }
    },
    deselect(user: DetailedUser) {
      const index = this.selected.findIndex(u => u.id === user.id);
      if (index >= 0) {
        const selected = [...this.selected];
        selected.splice(index, 1);
        this.$emit('selection', selected);
      }
    },
    set(user: DetailedUser) {
      const index = this.users.findIndex(u => u.id === user.id);
      if (index >= 0) {
        this.users.splice(index, 1, user);
      }
    },
    prepend(user: DetailedUser) {
      this.users.unshift(user);
      this.total += 1;
    },
    remove(user: DetailedUser) {
      const index = this.users.findIndex(u => u.id === user.id);
      if (index >= 0) {
        this.users.splice(index, 1);
        this.total -= 1;
      }
    },
  },
});
